import { KeyboardEvent, ReactNode } from "react";

interface SingleChoiceItem {
  id: string;
  label: string;
}

interface DefaultSingleTagProps<T extends SingleChoiceItem> {
  item: T;
  isSelected: boolean;
  selectedColor?: string;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  align?: "left" | "center";
}

const DefaultSingleTag = <T extends SingleChoiceItem>({
  item,
  isSelected,
  selectedColor = "rgba(124, 135, 194, 0.25)",
  onClick,
  onKeyDown,
  align = "left",
}: DefaultSingleTagProps<T>) => {
  return (
    <button
      className={`flex w-full items-center ${
        align === "left" ? "justify-start text-left" : "justify-center text-center"
      } rounded-full px-4 py-3 text-nearly-black shadow-subtle outline-none transition-all duration-200 ${isSelected ? "bg-brand-tint" : "bg-white/40"} `}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      type="button"
    >
      {item.label}
    </button>
  );
};

interface SingleChoiceTagPickerProps<T extends SingleChoiceItem> {
  items: T[];
  selectedId: string | null;
  onSelectionChangeRequest: (selectedItem: T | null) => void;
  selectedColor?: string;
  renderTag?: (props: DefaultSingleTagProps<T>) => ReactNode;
  ariaLabel?: string;
  align?: "left" | "center";
}

export const SingleChoiceTagPicker = <T extends SingleChoiceItem>({
  items,
  selectedId,
  onSelectionChangeRequest,
  selectedColor,
  renderTag = DefaultSingleTag,
  ariaLabel,
  align = "left",
}: SingleChoiceTagPickerProps<T>) => {
  const handleTagClick = (item: T) => {
    const isCurrentlySelected = selectedId === item.id;
    const newSelectedItem = isCurrentlySelected ? null : item;
    onSelectionChangeRequest(newSelectedItem);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, item: T) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleTagClick(item);
    }
  };

  return (
    <div className="flex flex-col gap-6" role="radiogroup" aria-label={ariaLabel}>
      {items.map((item) => {
        const isSelected = selectedId === item.id;

        return (
          <div key={item.id} className="w-full">
            {renderTag({
              item,
              isSelected,
              selectedColor,
              onClick: () => handleTagClick(item),
              onKeyDown: (event) => handleKeyDown(event, item),
              align,
            })}
          </div>
        );
      })}
    </div>
  );
};
